const MSAL_TRANSIENT_ERROR_CODES = new Set([
    'interaction_in_progress',
    'endpoints_resolution_error',
    'openid_config_error',
    'network_error',
])

export function isMsalInteractionInProgressError(error: unknown): boolean {
    if (!error || typeof error !== 'object') return false

    const e = error as { errorCode?: string; name?: string; message?: string }
    if (e.errorCode === 'interaction_in_progress') return true

    return (
        e.name === 'BrowserAuthError' &&
        Boolean(e.message?.includes('interaction_in_progress'))
    )
}

/** MSAL errors caused by flaky networks or concurrent auth — not app bugs. */
export function isMsalTransientAuthError(error: unknown): boolean {
    if (isMsalInteractionInProgressError(error)) return true
    if (!error || typeof error !== 'object') return false

    const e = error as { errorCode?: string; name?: string; message?: string }
    if (e.errorCode && MSAL_TRANSIENT_ERROR_CODES.has(e.errorCode)) return true

    const message = e.message ?? ''
    if (
        e.name === 'ClientAuthError' ||
        e.name === 'BrowserAuthError' ||
        e.name === 'AuthError'
    ) {
        return (
            message.includes('interaction_in_progress') ||
            message.includes('endpoints_resolution_error') ||
            message.includes('openid_config_error') ||
            message.includes('could not resolve endpoints') ||
            message.includes('Could not retrieve endpoints')
        )
    }

    return false
}

export function isBrowserExtensionRejection(reason: unknown): boolean {
    const message = reason instanceof Error ? reason.message : String(reason ?? '')
    return message.includes('Object Not Found Matching Id:')
}

export function isLegacyGoogleMapsLoadError(reason: unknown): boolean {
    const message = reason instanceof Error ? reason.message : String(reason ?? '')
    return /Could not load "(marker|util)"/.test(message)
}

/** Axios-style network failures without a response (also matches duck-typed errors). */
export function isLikelyNetworkError(error: unknown): boolean {
    if (!error || typeof error !== 'object') return false

    const e = error as {
        message?: string
        code?: string
        response?: unknown
        isAxiosError?: boolean
    }

    if (e.response !== undefined && e.response !== null) return false
    if (e.isAxiosError) return true

    return (
        e.code === 'ERR_NETWORK' ||
        e.code === 'ECONNABORTED' ||
        e.message === 'Network Error'
    )
}

/**
 * The event apps only serve Danish users, so traffic from other regions is
 * almost always bots/crawlers (e.g. Googlebot from US datacentres). We use the
 * client timezone and language as proxies — geo is only known server-side.
 *
 * Fails open: when we cannot determine the client region we keep the event.
 */
export function isNonDanishClient(): boolean {
    let timeZone = ''
    try {
        timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone ?? ''
    } catch {
        timeZone = ''
    }

    // Any European timezone → treat as legitimate (covers Danish users travelling/VPN in Europe).
    if (timeZone.startsWith('Europe/')) return false

    // Danish language anywhere → keep.
    const languages =
        typeof navigator !== 'undefined'
            ? [navigator.language, ...(navigator.languages ?? [])].filter(Boolean)
            : []
    if (languages.some((lang) => /^da(-|$)/i.test(lang))) return false

    // Known non-European timezone with no Danish signal → almost certainly a bot/crawler.
    if (timeZone) return true

    // Undetermined → keep the event.
    return false
}

export const SENTRY_IGNORE_ERROR_PATTERNS = [
    "Couldn't resolve component",
    'Failed to fetch dynamically imported module',
    'Importing a module script failed',
    'Load failed',
    'ChunkLoadError',
    'DYNAMIC_IMPORT_ERROR',
    'interaction_in_progress',
    'endpoints_resolution_error',
    'openid_config_error',
    'could not resolve endpoints',
    'Network Error',
    'Object Not Found Matching Id:',
    'Could not load "marker"',
    'Could not load "util"',
] as const
